import { Address } from "@origin-digital/address-lookup";

import { RichAddressInput } from "../../../crm/kraken/graphql-types/globalTypes";

import {
  floorTypeMapping,
  mapPafTypeToKrakenType,
  postalDeliveryTypeMapping,
  streetSuffixTypeMapping,
  streetTypeMapping,
  unitTypeMapping,
} from "../addressMappingHelpers";
import content from "./content.json";
import { AddressProp } from "./index";

/**
 * The following are actually enums in Kraken:
 *   flat_or_unit_type
 *   floor_or_level_type
 *   street_type
 *   street_suffix
 *   postal_delivery_type
 */
export interface KrakenStructuredStreetAddress {
  flat_or_unit_type?: string;
  flat_or_unit_number?: string;
  floor_or_level_type?: string;
  floor_or_level_number?: string;
  building_or_property_name?: string;
  location_descriptor?: string;
  house_number_1?: string;
  house_number_suffix_1?: string;
  house_number_2?: string;
  house_number_suffix_2?: string;
  lot_number?: string;
  street_name?: string;
  street_type?: string;
  street_suffix?: string;
  postal_delivery_type?: string;
  postal_delivery_number_prefix?: string;
  postal_delivery_number_value?: string;
  postal_delivery_number_suffix?: string;
}

export const convertQasAddressToRichAddressInput = ({
  allotmentNumber,
  allPostalDeliveryTypesNumber,
  allPostalDeliveryTypesType,
  buildingName,
  buildingLevelNumber,
  buildingLevelType,
  buildingNumberFirst,
  buildingNumberLast,
  city,
  dpid: deliveryPointIdentifier,
  postcode,
  state,
  streetNameOnly,
  streetType,
  streetTypeSuffix,
  unitNumber,
  unitType,
}: Address): RichAddressInput => {
  const structuredStreetAddress: KrakenStructuredStreetAddress = {
    flat_or_unit_type: mapPafTypeToKrakenType(
      unitType?.toUpperCase(),
      unitTypeMapping
    ),
    flat_or_unit_number: unitNumber ?? undefined,
    floor_or_level_type: mapPafTypeToKrakenType(
      buildingLevelType?.toUpperCase(),
      floorTypeMapping
    ),
    floor_or_level_number: buildingLevelNumber ?? undefined,
    building_or_property_name: buildingName ?? undefined,
    house_number_1: buildingNumberFirst ?? undefined,
    house_number_2: buildingNumberLast ?? undefined,
    lot_number: allotmentNumber ?? undefined,
    street_name: streetNameOnly ?? undefined,
    street_type: mapPafTypeToKrakenType(
      streetType?.toUpperCase(),
      streetTypeMapping
    ),
    street_suffix: mapPafTypeToKrakenType(
      streetTypeSuffix?.toUpperCase(),
      streetSuffixTypeMapping
    ),
    postal_delivery_type: mapPafTypeToKrakenType(
      allPostalDeliveryTypesType?.toUpperCase(),
      postalDeliveryTypeMapping
    ),
    postal_delivery_number_value: allPostalDeliveryTypesNumber ?? undefined,
  };

  // We manually set unitType to "LOT" if there is an allotment number
  // and no other unitType specified, as QAS does not include unitType "LOT"
  if (allotmentNumber && !unitType) {
    structuredStreetAddress.flat_or_unit_type = "LOT";
  }

  return {
    structuredStreetAddress,
    locality: city,
    administrativeArea: state,
    postalCode: postcode,
    country: "AU",
    deliveryPointIdentifier,
  };
};

export const generateMailto = (addresses: AddressProp[], info: string) => {
  const lineBreak = "%0D%0A";
  const accountDetails =
    addresses
      .map(
        (address) =>
          `${address.id} - ${address.address} ${content.changePrompt}`
      )
      .join(lineBreak) ?? "";

  return info.replace("{account-addresses}", accountDetails);
};
